import {
  useCallback, useEffect, useMemo, useRef, useState, type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ArrowLeft, ArrowRight, Volume2 } from 'lucide-react';
import { playSpeechStream } from '@evi/ui';
import { voiceOptionsForLocale, normalizeLocale } from '@evi/shared';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useAuthStore } from '@/store/auth';
import { useTourStore } from '@/store/tour';
import { useEvieVoiceStore, type EvieVoiceChoice } from '@/store/evieVoice';
import { synthesizeDriverSpeech } from '@/lib/api';
import { TourOrb } from './TourOrb';
import type { TourFlavor } from './steps';
import styles from './TourOverlay.module.css';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const FLAVORS: Array<{ id: TourFlavor; label: string; blurb: string }> = [
  { id: 'public', label: 'Public', blurb: 'I charge around town.' },
  { id: 'multifamily', label: 'Apartments & Condos', blurb: 'Multifamily settings with shared electrical infrastructure.' },
  { id: 'home', label: 'Home Charging', blurb: 'I have or want a charger at home.' },
  // Fleet is never a chip — it's auto-detected from the driver's fleet link
  // below; a driver can't self-select into a fleet (it needs an invite).
  { id: 'fleet', label: 'Fleet', blurb: 'My company drives.' },
];

// ── Intro copy (EVII's first-run introduction, before the chooser) ──────────
const INTRO_BULLETS = [
  'Help you charge anywhere',
  'Manage your rewards and account',
  'Fix problems and answer anything',
];

const HOLE_PAD = 8;

interface TourOverlayProps {
  /** App-wide navigate — the tour moves to each step's screen so EVII talks
   *  about what the driver is actually looking at. */
  onNavigate: (path: string) => void;
}

/**
 * The guided-tour overlay. Portals to document.body at z-index 1000 (above the
 * footer nav, exploding menu, and map sheet; below the EVII launcher / payment
 * modal / boot splash). A transparent tap-blocker stops stray app taps while
 * the tour runs; the spotlight cutout (a box-shadow "hole") dims everything but
 * the anchored control; a glass card carries EVII's line + Next/Back/Skip/Exit
 * + progress dots. The greet phase shows the driver-type chooser. Renders null
 * when the tour is inactive.
 */
export function TourOverlay({ onNavigate }: TourOverlayProps) {
  const active = useTourStore((s) => s.active);
  const phase = useTourStore((s) => s.phase);
  const steps = useTourStore((s) => s.steps);
  const stepIndex = useTourStore((s) => s.stepIndex);
  const start = useTourStore((s) => s.start);
  const next = useTourStore((s) => s.next);
  const back = useTourStore((s) => s.back);
  const branch = useTourStore((s) => s.branch);
  const exit = useTourStore((s) => s.exit);
  const reduced = useReducedMotion();
  const { t, i18n } = useTranslation('tour');
  // Data-driven voice options for the active language (unit 4). Empty for a
  // text-only locale (Armenian) -> the tour skips the voice step entirely.
  const voiceLocale = normalizeLocale(i18n.language);
  const voiceOpts = voiceOptionsForLocale(voiceLocale);
  const hasVoice = voiceOpts.length > 0;

  // Fleet-aware chooser: a driver already linked to a fleet skips the
  // question entirely (their answer is known) and goes straight into the
  // fleet tour; everyone else never sees Fleet as an option — joining a
  // fleet needs an invite, so the chip would only mislead.
  const fleetId = useAuthStore((s) => s.user?.fleet_id ?? null);
  const phaseNow = useTourStore((s) => s.phase);
  const activeNow = useTourStore((s) => s.active);
  useEffect(() => {
    if (activeNow && phaseNow === 'greet' && fleetId) {
      useTourStore.getState().start('fleet');
    }
  }, [activeNow, phaseNow, fleetId]);
  const flavors = fleetId ? FLAVORS : FLAVORS.filter((f) => f.id !== 'fleet');

  // ── Greet flow: intro → voice pick → chooser (all within phase 'greet') ────
  const userId = useAuthStore((s) => s.user?._id ?? null);
  const voice = useEvieVoiceStore((s) =>
    (userId && s.voiceByUser[userId]) || 'female',
  );
  const setVoiceChoice = useEvieVoiceStore((s) => s.setVoiceChoice);
  // A single-option locale (Chinese) resolves to its one voice (female); a
  // stored 'male' for such a locale shows as the available voice.
  const effectiveVoice: EvieVoiceChoice = voiceOpts.includes(voice) ? voice : (voiceOpts[0] ?? 'female');
  const [greetStep, setGreetStep] = useState<'intro' | 'voice' | 'chooser'>('intro');
  const [speaking, setSpeaking] = useState(false);
  const stopSpeakRef = useRef<(() => void) | null>(null);
  const introSpokenRef = useRef(false);

  // EVII speaks a line — best-effort. Autoplay may be blocked before a user
  // gesture (iOS); on any synth/playback failure we swallow the error and the
  // on-screen text remains the primary channel. `voiceOverride` lets the
  // voice-pick step speak the just-chosen voice immediately.
  const speak = useCallback(async (text: string, voiceOverride?: EvieVoiceChoice) => {
    // Stop any in-flight line first so lines don't overlap.
    stopSpeakRef.current?.();
    stopSpeakRef.current = null;
    try {
      const res = await synthesizeDriverSpeech(text, voiceOverride ?? voice, voiceLocale);
      const playback = playSpeechStream(res);
      stopSpeakRef.current = playback.stop;
      setSpeaking(true);
      await playback.done;
    } catch {
      /* muted / autoplay-blocked / synth error — text still shows */
    } finally {
      stopSpeakRef.current = null;
      setSpeaking(false);
    }
  }, [voice, voiceLocale]);

  // Reset the greet flow to the intro each time a fresh greet begins (the
  // overlay component stays mounted across tour open/close).
  useEffect(() => {
    if (phase === 'greet') {
      setGreetStep('intro');
      introSpokenRef.current = false;
    }
  }, [phase]);

  // Speak the intro once when the greet's intro card is showing.
  useEffect(() => {
    if (!active || phase !== 'greet' || greetStep !== 'intro' || introSpokenRef.current) return;
    introSpokenRef.current = true;
    void speak(t('intro.spoken'));
  }, [active, phase, greetStep, speak]);

  // Stop any playback when the tour closes or advances out of greet.
  useEffect(() => {
    if (!active || phase !== 'greet') {
      stopSpeakRef.current?.();
      stopSpeakRef.current = null;
    }
  }, [active, phase]);
  useEffect(() => () => { stopSpeakRef.current?.(); }, []);

  const step = phase === 'running' ? steps[stepIndex] : undefined;
  const anchor = step?.anchor ?? null;
  const orbAnchor = step?.orbAnchor ?? null;
  const stepEvent = step?.event;
  const route = step?.route;
  const [rect, setRect] = useState<Rect | null>(null);
  const [orbTarget, setOrbTarget] = useState<{ x: number; y: number } | null>(null);

  // Walk the app along with the tour: when a step names a route and the app
  // isn't on it, navigate there — so EVII never describes the expanding menu
  // while the driver is looking at Help.
  useEffect(() => {
    if (!active || phase !== 'running' || !route) return;
    if (window.location.pathname !== route) onNavigate(route);
  }, [active, phase, route, stepIndex, onNavigate]);

  // Step side-effect event (e.g. opening the Menu's My Charging sheet).
  // Dispatched after the step's navigation has had a beat to mount the page
  // that listens for it.
  useEffect(() => {
    if (!active || phase !== 'running' || !stepEvent) return;
    const t = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent(stepEvent));
    }, 420);
    return () => window.clearTimeout(t);
  }, [active, phase, stepEvent, stepIndex]);

  // Orb fly-to target: when a step names an orbAnchor, EVII moves to touch
  // that element (e.g. the rewards row). Same brief retry as the spotlight so
  // a just-navigated page's row can mount first.
  useEffect(() => {
    if (!active || !orbAnchor) {
      setOrbTarget(null);
      return;
    }
    let tries = 0;
    let retryTimer: number | null = null;
    const measure = () => {
      const el = document.querySelector(orbAnchor);
      if (!el) {
        setOrbTarget(null);
        if (tries < 20) {
          tries += 1;
          retryTimer = window.setTimeout(measure, 120);
        }
        return;
      }
      const r = el.getBoundingClientRect();
      // Hover just above the row's right end — "touching" it without covering
      // the label.
      setOrbTarget({ x: r.left + r.width * 0.82, y: r.top + 6 });
    };
    measure();
    // Settle re-measure (same rationale as the spotlight): the page-enter
    // slide means a rect captured immediately can be mid-flight, which parked
    // the orb in the wrong spot. Re-measure after the transition finishes.
    const settleTimer = window.setTimeout(measure, 700);
    window.addEventListener('resize', measure);
    return () => {
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      window.clearTimeout(settleTimer);
      window.removeEventListener('resize', measure);
    };
  }, [active, orbAnchor, stepIndex]);

  // Measure the anchored element for the spotlight cutout; re-measure on step
  // change and on resize/scroll so the hole tracks the target. After a step
  // navigation the anchor mounts a few frames later, so retry briefly until it
  // appears instead of giving up on the first miss.
  useEffect(() => {
    if (!active || !anchor) {
      setRect(null);
      return;
    }
    let tries = 0;
    let retryTimer: number | null = null;
    const measure = () => {
      const el = document.querySelector(anchor);
      if (!el) {
        setRect(null);
        if (tries < 20) {
          tries += 1;
          retryTimer = window.setTimeout(measure, 120);
        }
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    // Settle re-measure: the app's page-enter animation slides content for
    // ~280ms, so a rect captured immediately after a step navigation can be
    // mid-flight. Re-measure once the transition has finished.
    const settleTimer = window.setTimeout(measure, 700);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      window.clearTimeout(settleTimer);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [active, anchor, stepIndex]);

  // Lock body scroll while the tour runs (mirrors BottomSheet).
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);

  const progress = useMemo(() => {
    if (phase === 'greet') return 0;
    if (steps.length <= 1) return 1;
    return stepIndex / (steps.length - 1);
  }, [phase, steps.length, stepIndex]);

  if (!active) return null;

  const hole: Rect | null = rect
    ? {
        top: rect.top - HOLE_PAD,
        left: rect.left - HOLE_PAD,
        width: rect.width + HOLE_PAD * 2,
        height: rect.height + HOLE_PAD * 2,
      }
    : null;

  return createPortal(
    <div className={styles.root} role="dialog" aria-modal="true" aria-label={t('ariaLabel')}>
      {/* Transparent full-screen tap-blocker: stops stray app taps behind the
          tour without dimming (the dim is the scrim/hole below). */}
      <div className={styles.tapBlock} />

      {/* Visual dim: a full scrim, or a box-shadow cutout around the anchor.
          Steps that open live UI to look at (noDim) skip it entirely. */}
      {step?.noDim ? null : hole ? (
        <div
          className={reduced ? `${styles.hole} ${styles.holeStatic}` : styles.hole}
          style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
        />
      ) : (
        <div className={styles.scrim} />
      )}

      <button type="button" className={styles.exit} onClick={exit} aria-label={t('exitTour')}>
        <X size={18} />
      </button>

      <TourOrb progress={progress} reduced={reduced} overridePx={orbTarget} />

      {phase === 'greet' ? (
        <div className={`${styles.card} ${styles.cardCenter}`} data-reduced={reduced || undefined}>
          {greetStep === 'intro' ? (
            <>
              <p className={styles.eyebrow}>
                {t('intro.eyebrow')}
                {speaking && <Volume2 size={13} className={styles.speakingIcon} aria-hidden />}
              </p>
              <h2 className={styles.title}>{t('intro.title')}</h2>
              <p className={styles.body}>{t('intro.body1')}</p>
              <ul className={styles.introList}>
                {INTRO_BULLETS.map((b, i) => (
                  <li key={b}>{t(`intro.bullets.${i}`)}</li>
                ))}
              </ul>
              <p className={styles.body}>
                {t('intro.body2Pre')}{' '}
                <strong>{t('intro.whatsNew')}</strong>{t('intro.body2Post')}
              </p>
              <div className={styles.controls}>
                <button type="button" className={styles.ghost} onClick={exit}>
                  {t('intro.skip')}
                </button>
                <div className={styles.navBtns}>
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={() => void speak(t('intro.spoken'))}
                  >
                    <Volume2 size={16} /> {t('intro.hear')}
                  </button>
                  <button
                    type="button"
                    className={styles.primary}
                    onClick={() => setGreetStep(hasVoice ? 'voice' : 'chooser')}
                  >
                    {t('intro.next')} <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </>
          ) : greetStep === 'voice' ? (
            <>
              <p className={styles.eyebrow}>{t('voice.eyebrow')}</p>
              <h2 className={styles.title}>{t('voice.title')}</h2>
              <p className={styles.body}>
                {t('voice.body')}
              </p>
              <div className={styles.chips}>
                {voiceOpts.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className={effectiveVoice === id ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                    aria-pressed={effectiveVoice === id}
                    onClick={() => {
                      // Persists server-side via the evieVoice store (Tour U1).
                      setVoiceChoice(userId, id);
                      void speak(t('voice.sample'), id);
                    }}
                  >
                    <span className={styles.chipLabel}>
                      {t(`voice.persona.${id}`)}
                      {effectiveVoice === id && speaking && (
                        <Volume2 size={12} className={styles.speakingIcon} aria-hidden />
                      )}
                    </span>
                    <span className={styles.chipBlurb}>{t(`voice.options.${id}.sub`)}</span>
                  </button>
                ))}
              </div>
              <div className={styles.controls}>
                <button
                  type="button"
                  className={styles.ghost}
                  onClick={() => setGreetStep('intro')}
                >
                  <ArrowLeft size={16} /> {t('voice.back')}
                </button>
                <button
                  type="button"
                  className={styles.primary}
                  onClick={() => setGreetStep('chooser')}
                >
                  {t('voice.continue')} <ArrowRight size={16} />
                </button>
              </div>
            </>
          ) : (
            <>
              <p className={styles.eyebrow}>{t('intro.eyebrow')}</p>
              <h2 className={styles.title}>{t('chooser.title')}</h2>
              <p className={styles.body}>
                {t('chooser.body')}
              </p>
              <div className={styles.chips}>
                {flavors.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={styles.chip}
                    onClick={() => start(f.id)}
                  >
                    <span className={styles.chipLabel}>{t(`flavors.${f.id}.label`)}</span>
                    <span className={styles.chipBlurb}>{t(`flavors.${f.id}.blurb`)}</span>
                  </button>
                ))}
              </div>
              <div className={styles.controls}>
                <button
                  type="button"
                  className={styles.ghost}
                  onClick={() => setGreetStep(hasVoice ? 'voice' : 'intro')}
                >
                  <ArrowLeft size={16} /> {t('chooser.back')}
                </button>
              </div>
            </>
          )}
        </div>
      ) : step ? (
        <div
          className={`${styles.card} ${
            step.cardAt === 'top'
              ? styles.cardTop
              : hole
                ? styles.cardAnchored
                : styles.cardCenter
          }`}
          style={step.cardAt !== 'top' && hole ? cardPositionForHole(hole) : undefined}
          data-reduced={reduced || undefined}
        >
          <h2 className={styles.title}>{step.title}</h2>
          {step.body && <p className={styles.body}>{step.body}</p>}
          {step.actionHint && <p className={styles.hint}>{step.actionHint}</p>}

          {/* Fork step: choice buttons stand in for Next (the multifamily
              "did your building enable shared charging?" question). */}
          {step.choices && (
            <div className={styles.choices}>
              {step.choices.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  className={styles.choice}
                  onClick={() => branch(c.steps)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          <div className={styles.dots} aria-hidden>
            {steps.map((s, i) => (
              <span
                key={s.id}
                className={i === stepIndex ? `${styles.dot} ${styles.dotOn}` : styles.dot}
              />
            ))}
          </div>

          <div className={styles.controls}>
            <button type="button" className={styles.ghost} onClick={exit}>
              {t('controls.skipTour')}
            </button>
            <div className={styles.navBtns}>
              {stepIndex > 0 && (
                <button type="button" className={styles.secondary} onClick={back}>
                  <ArrowLeft size={16} /> {t('controls.back')}
                </button>
              )}
              {!step.choices && (
                <button type="button" className={styles.primary} onClick={next}>
                  {stepIndex >= steps.length - 1 ? (
                    t('controls.done')
                  ) : (
                    <>
                      {t('controls.next')} <ArrowRight size={16} />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

/**
 * Place the tooltip card below the anchor when there's room, else above.
 * Horizontally centered and clamped to the viewport by the CSS max-width +
 * side margins.
 */
function cardPositionForHole(hole: Rect): CSSProperties {
  const below = hole.top + hole.height + 12;
  const spaceBelow = window.innerHeight - below;
  if (spaceBelow > 240) {
    return { top: below, left: '50%', transform: 'translateX(-50%)' };
  }
  return { top: Math.max(12, hole.top - 12), left: '50%', transform: 'translate(-50%, -100%)' };
}
